import { useState, useRef, useCallback, useEffect } from 'react';
import type { MouseEventHandler } from 'react';
import { IconAnimationTriggers } from '../Button.types';
import { TRANSITION_DURATION } from '../constants';

interface UseButtonInteractionStateResult {
  active: boolean;
  onMouseEnter: MouseEventHandler;
  onMouseLeave: MouseEventHandler;
  onIconAnimationClick?: MouseEventHandler;
}

export function useButtonInteractionState(
  iconAnimationTriggers: Array<IconAnimationTriggers>,
  onAnimationForward: () => void,
  onAnimationBackward: () => void,
  propsOnMouseEnter?: MouseEventHandler,
  propsOnMouseLeave?: MouseEventHandler,
): UseButtonInteractionStateResult {
  const [active, setActive] = useState(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isForwardRef = useRef(false);
  const hasHoverTrigger = iconAnimationTriggers.includes(
    IconAnimationTriggers.HOVER,
  );
  const hasClickTrigger = iconAnimationTriggers.includes(
    IconAnimationTriggers.CLICK,
  );

  useEffect(() => {
    isForwardRef.current = false;
  }, [hasClickTrigger, hasHoverTrigger]);

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const onMouseEnter = useCallback<MouseEventHandler>(
    event => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
      setActive(true);
      if (hasHoverTrigger) {
        onAnimationForward();
      }
      propsOnMouseEnter?.(event);
    },
    [hasHoverTrigger, onAnimationForward, propsOnMouseEnter],
  );

  const onMouseLeave = useCallback<MouseEventHandler>(
    event => {
      animationTimeoutRef.current = setTimeout(() => {
        setActive(false);
      }, TRANSITION_DURATION);
      if (hasHoverTrigger) {
        onAnimationBackward();
      }
      propsOnMouseLeave?.(event);
    },
    [hasHoverTrigger, onAnimationBackward, propsOnMouseLeave],
  );

  const onIconAnimationClick = useCallback<MouseEventHandler>(() => {
    if (!hasClickTrigger) {
      return;
    }

    if (isForwardRef.current) {
      onAnimationBackward();
    } else {
      onAnimationForward();
    }

    isForwardRef.current = !isForwardRef.current;
  }, [hasClickTrigger, onAnimationBackward, onAnimationForward]);

  return {
    active,
    onMouseEnter,
    onMouseLeave,
    onIconAnimationClick: hasClickTrigger ? onIconAnimationClick : undefined,
  };
}
